import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import type { DBInvoice } from "@/lib/db-types";
import { formatNOK, formatInvoiceType } from "@/lib/format";
import { effectiveInvoiceType } from "@/lib/invoice-plan";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";
import { CollapsibleCard } from "@/components/shared/CollapsibleCard";
import { useLinkUpsellToInvoice } from "@/hooks/use-upsells";
import { usePermissions } from "@/hooks/use-permissions";
import type { UpsellSummary } from "@/hooks/use-invoice-calculations";

interface InvoicePrefill {
  amount?: number;
  description?: string;
  type?: string;
  related_invoice_id?: string;
  lines?: { description: string; quantity: number; unit_price: number }[];
  upsellIds?: string[];
}

interface RemainingToInvoiceCardProps {
  projectId: string;
  projectName: string;
  invoices: DBInvoice[];
  uninvoicedUpsells: UpsellSummary[];
  allUpsellsCount: number;
  remainingContract: number;
  contractExclUpsells: number;
  invoicedExclVat: number;
  uninvoicedUpsellTotal: number;
  totalRemaining: number;
  onOpenCreate: (prefill?: InvoicePrefill) => void;
  /** VIB-722c: gate "Opprett sluttfaktura"-knappen (create_invoice). */
  canCreateInvoice?: boolean;
  /**
   * VIB-1182: true når planlagte delfakturaer (oppstart/milepæl) fortsatt
   * gjenstår. Da skjules "Opprett sluttfaktura" så den ikke konkurrerer med
   * oppstarts-/milepæl-knappene (forvirret brukerne i feedback).
   */
  plannedInvoicesRemaining?: boolean;
}

const NONE_VALUE = "__none__";

/**
 * "Gjenstående til fakturering"-kortet: viser tre stat-bokser (rest kontrakt,
 * ufakturert mersalg, totalt) + en liste over ufakturerte mersalgsposter
 * som superadmin kan knytte til eksisterende fakturaer.
 */
export function RemainingToInvoiceCard({
  projectId,
  projectName,
  invoices,
  uninvoicedUpsells,
  allUpsellsCount,
  remainingContract,
  contractExclUpsells,
  invoicedExclVat,
  uninvoicedUpsellTotal,
  totalRemaining,
  onOpenCreate,
  canCreateInvoice = true,
  plannedInvoicesRemaining = false,
}: RemainingToInvoiceCardProps) {
  const { isSuperadmin } = usePermissions();
  const linkUpsellMut = useLinkUpsellToInvoice();

  return (
    <CollapsibleCard
      title="Gjenstående til fakturering"
      icon={<SrIcon name="receipt" className="w-5 h-5 text-primary" />}
      headerExtra={
        <span className="text-sm font-medium text-muted-foreground ml-2">
          {formatNOK(totalRemaining)}
        </span>
      }
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
        <div className="rounded-lg border bg-background p-3">
          <p className="text-xs text-muted-foreground mb-1">Rest kontrakt</p>
          <p
            className={cn(
              "text-lg font-bold tabular-nums",
              remainingContract <= 0 ? "text-green-600" : "text-foreground",
            )}
          >
            {formatNOK(remainingContract)}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {formatNOK(contractExclUpsells)} grunntilbud, {formatNOK(invoicedExclVat)} fakturert (eks. mva)
          </p>
        </div>
        <div className="rounded-lg border bg-background p-3">
          <p className="text-xs text-muted-foreground mb-1">Ufakturert mersalg</p>
          <p
            className={cn(
              "text-lg font-bold tabular-nums",
              uninvoicedUpsellTotal === 0 ? "text-muted-foreground" : "text-foreground",
            )}
          >
            {formatNOK(uninvoicedUpsellTotal)}
          </p>
          <p className="text-[11px] text-muted-foreground">
            {uninvoicedUpsells.length} av {allUpsellsCount} poster
          </p>
        </div>
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
          <p className="text-xs text-muted-foreground mb-1">Totalt gjenstående</p>
          <p className="text-lg font-bold tabular-nums">{formatNOK(totalRemaining)}</p>
          <div className="flex gap-1.5 mt-2 flex-wrap">
            {totalRemaining > 0 && canCreateInvoice && !plannedInvoicesRemaining && (
              <Button
                size="sm"
                variant="default"
                className="h-7 text-xs px-3"
                onClick={() => {
                  // Sluttfaktura med mersalg som egne linjer: grunntilbud-rest
                  // som én linje + én linje per ufakturert mersalg. Mersalgene
                  // markeres fakturert (added_to_invoice_id) via upsellIds.
                  // unit_price = amount_excl_vat (kundepris inkl. påslag/rabatt).
                  const baseRemaining = totalRemaining - uninvoicedUpsellTotal;
                  const lines: { description: string; quantity: number; unit_price: number }[] = [];
                  if (baseRemaining > 0) {
                    lines.push({
                      description: "Sluttoppgjør i henhold til avtale.",
                      quantity: 1,
                      unit_price: baseRemaining,
                    });
                  }
                  for (const u of uninvoicedUpsells) {
                    lines.push({ description: u.description, quantity: 1, unit_price: u.amount_excl_vat });
                  }
                  onOpenCreate({
                    amount: totalRemaining,
                    description: `Sluttfaktura ${projectName}`,
                    type: "final",
                    lines: lines.length > 0 ? lines : undefined,
                    upsellIds:
                      uninvoicedUpsells.length > 0 ? uninvoicedUpsells.map((u) => u.id) : undefined,
                  });
                }}
              >
                Opprett sluttfaktura ({formatNOK(totalRemaining)})
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Ufakturerte mersalgsposter */}
      {uninvoicedUpsells.length > 0 && (
        <div className="mt-2">
          <p className="text-xs font-medium text-muted-foreground mb-1.5">
            Ufakturerte mersalgsposter
            {isSuperadmin && (
              <span className="font-normal italic ml-1">
                — knytt til en eksisterende faktura hvis den allerede er sendt
              </span>
            )}
            :
          </p>
          <div className="space-y-1">
            {uninvoicedUpsells.map((u) => (
              <div
                key={u.id}
                className="flex items-center justify-between gap-2 text-xs px-2 py-1.5 rounded bg-muted/30"
              >
                <span className="flex-1 min-w-0 truncate">{u.description}</span>
                <span className="tabular-nums font-medium">{formatNOK(u.amount_excl_vat)}</span>
                {isSuperadmin && invoices.length > 0 && (
                  <Select
                    value={NONE_VALUE}
                    onValueChange={(v) =>
                      linkUpsellMut.mutate({
                        upsell_id: u.id,
                        project_id: projectId,
                        invoice_id: v === NONE_VALUE ? null : v,
                      })
                    }
                  >
                    <SelectTrigger className="h-7 text-[11px] w-[200px]">
                      <SelectValue placeholder="Knytt til faktura..." />
                    </SelectTrigger>
                    <SelectContent>
                      {invoices.map((inv) => (
                        <SelectItem key={inv.id} value={inv.id} className="text-xs">
                          {inv.invoice_no ? `#${inv.invoice_no}` : "(uten nr)"}
                          {" — "}
                          {formatInvoiceType(effectiveInvoiceType(inv), inv.poweroffice_status)}
                          {" — "}
                          {formatNOK(inv.amount_excl_vat ?? 0)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </CollapsibleCard>
  );
}
